/**
 * Centralized date/datetime formatting for Atlas. All display strings render
 * in **Pacific/Auckland** (NZDT/NZST) regardless of the viewer's OS locale,
 * matching the operator's fleet timezone. Outside-NZ staff (QA, partners) see
 * the same wall-clock value as the depot.
 *
 * For inputs, use {@link toDateInputValue}/{@link toDateTimeInputValue} —
 * they emit ISO-ish strings (YYYY-MM-DD / YYYY-MM-DDTHH:mm) for HTML
 * date/datetime-local pickers. The picker itself renders in the browser's
 * local timezone; the value attribute we emit is the NZ wall-clock so a
 * pre-filled input shows the same day the rest of the UI shows.
 */

const SHORT_MONTH = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const NZ_TZ = 'Pacific/Auckland';

function toDate(v: unknown): Date | null {
  if (v == null || v === '') return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const d = new Date(v as any);
  return Number.isNaN(d.getTime()) ? null : d;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

// Decompose a Date into NZ-local calendar/clock parts. Intl.formatToParts is
// the only locale-independent way to read wall-clock components in a chosen
// timezone — `Date.getHours()` etc. read the JS runtime's local timezone.
const NZ_PARTS_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: NZ_TZ,
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit',
  hour12: false,
});
function nzParts(d: Date): { year: number; month: number; day: number; hour: number; minute: number; second: number } {
  const parts = NZ_PARTS_FMT.formatToParts(d);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  // Some runtimes emit hour '24' at midnight; coerce to 0.
  const hour = get('hour');
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: hour === 24 ? 0 : hour,
    minute: get('minute'),
    second: get('second'),
  };
}

export function fmtDate(v: unknown, fallback = '—'): string {
  const d = toDate(v);
  if (!d) return fallback;
  const p = nzParts(d);
  return `${pad2(p.day)}/${SHORT_MONTH[p.month - 1]}/${p.year}`;
}

export function fmtDateTime(v: unknown, fallback = '—'): string {
  const d = toDate(v);
  if (!d) return fallback;
  const p = nzParts(d);
  return `${pad2(p.day)}/${SHORT_MONTH[p.month - 1]}/${p.year} ${pad2(p.hour)}:${pad2(p.minute)}`;
}

export function toDateInputValue(v: unknown): string {
  const d = toDate(v);
  if (!d) return '';
  const p = nzParts(d);
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}`;
}

export function toDateTimeInputValue(v: unknown): string {
  const d = toDate(v);
  if (!d) return '';
  const p = nzParts(d);
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}T${pad2(p.hour)}:${pad2(p.minute)}`;
}

/**
 * Construct a UTC Date that, viewed in Pacific/Auckland, has the given
 * wall-clock components. DST-aware via an iterative-correction pass — covers
 * the spring-forward gap / fall-back overlap landing on the requested clock.
 */
function nzWallToUtc(y: number, mo: number, d: number, h: number, mi: number): Date {
  let cand = new Date(Date.UTC(y, mo, d, h, mi, 0, 0));
  for (let i = 0; i < 2; i++) {
    const p = nzParts(cand);
    const drift = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, 0) - Date.UTC(y, mo, d, h, mi, 0);
    if (drift === 0) break;
    cand = new Date(cand.getTime() - drift);
  }
  return cand;
}

/**
 * Parse a `<input type="datetime-local">` string ("YYYY-MM-DDTHH:mm") as a
 * NZ wall-clock moment and return the corresponding UTC Date. Use this in
 * onChange handlers paired with {@link toDateTimeInputValue} so a date entered
 * in the picker means the same NZ time regardless of the browser's tz.
 */
export function parseNzWallToUtc(s: string): Date | null {
  if (!s) return null;
  const m = s.trim().match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!m) return null;
  const [, y, mo, d, h, mi] = m;
  return nzWallToUtc(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi));
}

/* ── NZ calendar-day arithmetic ──────────────────────────────────────────
 * Day boundaries and day stepping on the **depot's** calendar. `setHours(0,0,0,0)`
 * / `setDate(getDate()+n)` operate in the JS runtime's timezone, so they pick a
 * different day for anyone outside NZ — and the day they pick disagrees with
 * everything {@link fmtDate} and {@link toDateInputValue} render. Use these
 * instead wherever a UI buckets, labels, or queries "a day".
 */

/** Midnight opening the NZ calendar day that contains `d`. */
export function startOfNzDay(d: Date): Date {
  const p = nzParts(d);
  return nzWallToUtc(p.year, p.month - 1, p.day, 0, 0);
}

/** Shift by whole NZ calendar days. Rollover goes through `Date.UTC` (immune to
 *  the host tz) and the result is re-anchored to NZ midnight, so a DST
 *  transition inside the span cannot drop or duplicate a day. */
export function addNzDays(d: Date, n: number): Date {
  const p = nzParts(d);
  const t = new Date(Date.UTC(p.year, p.month - 1, p.day + n));
  return nzWallToUtc(t.getUTCFullYear(), t.getUTCMonth(), t.getUTCDate(), 0, 0);
}

/** Final millisecond of the NZ calendar day that contains `d`. Derived from the
 *  next day's midnight rather than a literal 23:59:59.999, so it stays exact
 *  across a DST shift that makes the day 23 or 25 hours long. */
export function endOfNzDay(d: Date): Date {
  return new Date(addNzDays(startOfNzDay(d), 1).getTime() - 1);
}

/** Day of the week on the NZ calendar (0=Sun … 6=Sat). */
export function nzDayOfWeek(d: Date): number {
  const p = nzParts(d);
  return new Date(Date.UTC(p.year, p.month - 1, p.day)).getUTCDay();
}

// Lower-cased short + long month name lookup so DD/MMM/YYYY input parsing is
// case-insensitive and tolerates both `Jun` and `June` style entries.
const MONTH_NAME_TO_INDEX: Record<string, number> = (() => {
  const map: Record<string, number> = {};
  SHORT_MONTH.forEach((m, i) => { map[m.toLowerCase()] = i; });
  ['january','february','march','april','may','june','july','august','september','october','november','december']
    .forEach((m, i) => { map[m] = i; });
  return map;
})();

/** Parse a user-typed `DD/MM/YYYY`, `DD/MMM/YYYY`, or either with `HH:mm`
 *  back to a Date. Accepts numeric months (backwards compat with the prior
 *  display format) and short/long month names case-insensitively. Returns a
 *  Date constructed in the JS runtime's local tz — same as the old behavior
 *  to keep CRUD filter forms working without further changes. */
export function parseDDMMYYYY(s: string): Date | null {
  if (!s) return null;
  const trimmed = s.trim();
  // Numeric month form: DD/MM/YYYY [HH:mm]
  let m = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T](\d{1,2}):(\d{2}))?$/);
  if (m) {
    const [, dd, mm, yyyy, hh, mi] = m;
    const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd), Number(hh ?? 0), Number(mi ?? 0));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  // Month-name form: DD/Mon/YYYY [HH:mm]
  m = trimmed.match(/^(\d{1,2})\/([A-Za-z]+)\/(\d{4})(?:[ T](\d{1,2}):(\d{2}))?$/);
  if (m) {
    const [, dd, monName, yyyy, hh, mi] = m;
    const monIdx = MONTH_NAME_TO_INDEX[monName.toLowerCase()];
    if (monIdx == null) return null;
    const d = new Date(Number(yyyy), monIdx, Number(dd), Number(hh ?? 0), Number(mi ?? 0));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}
